import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useDemoInvitesEnv } from "../DemoInvites/useDemoInvitesEnv";
import { getDemoAccessConfig, listApplicants } from "./api";
import { ApplicantDetail } from "./ApplicantDetail";
import { CompetitorsPanel } from "./CompetitorsPanel";
import { ConfidenceBadge, FLAG_LABELS, STATUS_LABELS, StatusBadge, VerdictBadge, errorMessage, formatTimestamp } from "./shared";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "../ReferralEligibility/ReferralEligibility.css";
import "../DemoInvites/DemoInvites.css";
import "./DemoAccess.css";

const SEARCH_DEBOUNCE_MS = 300;
const TABS = [
  { key: "applicants", label: "Applications" },
  { key: "competitors", label: "Competitor registry" },
];

function OnOff({ label, isOn }) {
  return (
    <span className={`ref-badge ${isOn ? "da-flag-on" : "da-flag-off"}`}>
      {label}: {isOn ? "on" : "off"}
    </span>
  );
}

function ConfigStrip({ env }) {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    setConfig(null);
    setError(null);
    const load = async () => {
      try {
        const response = await getDemoAccessConfig(env);
        if (isCurrent) {
          setConfig(response.config || null);
        }
      } catch (loadError) {
        if (isCurrent) {
          setError(errorMessage(loadError));
        }
      }
    };
    load();

    return () => {
      isCurrent = false;
    };
  }, [env]);

  if (error) {
    return <div className="ref-error" role="alert">Could not load screening configuration: {error}</div>;
  }
  if (!config) {
    return <div className="da-config ref-muted">Loading screening configuration…</div>;
  }

  return (
    <section className="da-config" aria-label="Screening configuration">
      <div className="da-config-badges">
        <OnOff label="Identity check" isOn={config.identityCheckEnabled} />
        <OnOff label="AI web check" isOn={config.webCheckEnabled} />
        <OnOff label="AI web check configured" isOn={config.webCheckConfigured} />
        <OnOff label="Auto-approval" isOn={config.autoApprovalEnabled} />
        <OnOff label="LinkedIn configured" isOn={config.linkedinConfigured} />
        <span className="ref-muted">
          Policy {config.policyVersion} · web check model {config.webCheckModel || "—"} · legacy sessions: {config.legacySessionPolicy}
        </span>
      </div>
      {config.issues?.length ? (
        <ul className="da-config-issues">
          {config.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ApplicantsPanel({ env }) {
  const [applicants, setApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("");
  const [flag, setFlag] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const latestRequestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);
    setError(null);
    try {
      const response = await listApplicants(env, { status, flag, q: debouncedQuery });
      if (latestRequestRef.current === requestId) {
        setApplicants(response.applicants || []);
      }
    } catch (loadError) {
      if (latestRequestRef.current === requestId) {
        setError(errorMessage(loadError));
      }
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [env, status, flag, debouncedQuery]);

  useEffect(() => {
    load();
  }, [load]);

  // A response that lands after the tab closed or a newer search started is dropped.
  useEffect(
    () => () => {
      latestRequestRef.current += 1;
    },
    [],
  );

  return (
    <>
      <div className="ref-table-controls da-filters">
        <input
          className="ref-input ref-filter"
          placeholder="Search by name, email or company"
          aria-label="Search applicants"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select className="ref-input da-select" aria-label="Filter by status" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select className="ref-input da-select" aria-label="Filter by competitor flag" value={flag} onChange={(event) => setFlag(event.target.value)}>
          <option value="">Any competitor flag</option>
          {Object.entries(FLAG_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button type="button" className="ref-btn ref-btn-ghost" onClick={load} disabled={isLoading}>
          Refresh
        </button>
        <span className="ref-muted">{applicants.length} applications</span>
      </div>

      {error ? <div className="ref-error" role="alert">{error}</div> : null}

      {isLoading ? (
        <div className="ref-empty">Loading applications…</div>
      ) : (
        <div className="ref-table-wrapper">
          {applicants.length === 0 ? (
            <div className="ref-empty">No applications match.</div>
          ) : (
            <table className="ref-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>AI web check</th>
                  <th>Screening</th>
                  <th>Applied</th>
                  <th>Last checked</th>
                  <th className="ref-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map((applicant) => (
                  <tr key={applicant.id}>
                    <td>
                      <div className="ref-org-name">{applicant.name || "—"}</div>
                      <div className="ref-muted ref-org-id">{applicant.workEmail || "No work email yet"}</div>
                    </td>
                    <td>
                      {applicant.companyName || "—"}
                    </td>
                    <td>
                      <StatusBadge status={applicant.status} />
                    </td>
                    <td>
                      <VerdictBadge verdict={applicant.verdict} />
                      <div>
                        <ConfidenceBadge confidence={applicant.confidence} />
                      </div>
                    </td>
                    <td>
                      {(applicant.flags || []).map((value) => (
                        <span key={value} className="ref-badge da-flag-competitor">
                          {FLAG_LABELS[value] || value}
                        </span>
                      ))}
                      <div className="di-note da-reason-summary" title={(applicant.reasonSummary || []).join(", ")}>
                        {(applicant.reasonSummary || []).join(", ") || "—"}
                      </div>
                    </td>
                    <td>{formatTimestamp(applicant.createdAt)}</td>
                    <td>{formatTimestamp(applicant.lastCheckedAt)}</td>
                    <td className="ref-actions-col">
                      <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" onClick={() => setSelectedId(applicant.id)}>
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selectedId ? <ApplicantDetail env={env} applicantId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} /> : null}
    </>
  );
}

const DemoAccessPage = ({ onReady }) => {
  const [env, setEnv] = useDemoInvitesEnv();
  const [tab, setTab] = useState("applicants");

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    <div className="ref-page">
      <ToastContainer position="top-right" autoClose={3500} />

      <div className="ref-page-header">
        <div>
          <p className="ref-eyebrow">Sales</p>
          <h1 className="ref-page-title">Demo Access</h1>
          <p className="ref-page-subtitle">
            Identity and competitor screening for demo invites: LinkedIn sign-in, a verified work email and an AI web check, with a decision for every applicant.
          </p>
        </div>
        <div className="ref-header-controls">
          <EnvToggle env={env} onChange={setEnv} />
        </div>
      </div>

      {env !== "dev" ? (
        <p className="ref-preview-note">
          The live demo app reads invites from Dev. Applications in {env === "prod" ? "Prod" : "Local"} are not from the live demo.
        </p>
      ) : null}

      <ConfigStrip env={env} />

      <div className="ref-range-toggle da-tabs" role="tablist" aria-label="Demo access sections">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`ref-range-btn ${tab === entry.key ? "ref-range-btn-active" : ""}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "applicants" ? <ApplicantsPanel env={env} /> : null}
      {tab === "competitors" ? <CompetitorsPanel env={env} /> : null}
    </div>
  );
};

export default DemoAccessPage;
